/**
 * `useHtml5BoardDnd` — the drag state machine the two board screens share
 * (INVENTORY-DESKTOP §5; lane D3).
 *
 * `dragHtml5.ts` (D0) owns the parts that are the same for everyone: the ghost
 * node, the window listeners, the auto-scroll loop, the wrap-aware hit-test and
 * the `beforeId → idx` adapter. What it deliberately does NOT own is the state
 * — `dragId` / `overRow` / `overBeforeId` — because the prototype kept those in
 * component state and the store's `DragState` / `DragOver` are index-based and
 * mobile-specific (§5.6). This hook is that state, plus the three prop bundles
 * the template's `g.dragStart` / `r.over|leave|drop` / `todoOver|todoDrop`
 * expand to.
 *
 *   const dnd = useHtml5BoardDnd();
 *
 *   // a capsule (the ghost VM is a thunk: the resolved cover is only known
 *   // inside the capsule, after `useArt` has walked the ladder)
 *   <div {...dnd.dragProps(g.id, () => ({ id: g.id, nameUp: g.nameUp, coverBg, fall: g.fall }))} />
 *
 *   // a row — `rowIds` must be the UNFILTERED target row (`selectors.d3.ts`)
 *   <div {...dnd.rowProps('y', (id, before) => void setIntAt(id, 'y', beforeIdToIdx(rowIds, id, before)))} />
 *
 *   // an order-less drop zone (the GOTY TO-DO rail)
 *   <div {...dnd.zoneProps((id) => void unplaceTier(id))} />
 *
 * THREE THINGS THAT LOOK LIKE DETAILS AND ARE NOT
 *
 *  1. **Every dispatch is guarded.** The prototype wrote
 *     `if (this.state.overId !== target) this.setState(...)`, and it has to
 *     stay that way: `dragover` fires continuously (roughly every 50-350ms per
 *     spec, far more often in practice), and an unguarded dispatch would
 *     re-render the whole board on every one. The reducer returns the SAME
 *     object when nothing changed, so React bails out.
 *
 *  2. **Handlers read state through a ref, not the render closure.** `drop`
 *     arrives after a burst of `dragover` dispatches; reading `state` captured
 *     at render time can miss the last one. The ref is written during render
 *     and is always current.
 *
 *  3. **`dataTransfer` is the fallback, never the source of truth** (§10.5
 *     risk 2). Firefox and Safari differ on `setDragImage` and on when
 *     `getData` is readable; `dragId` in React state is what actually carries
 *     the drag, exactly as the prototype had it.
 */

import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import type { DragEvent as ReactDragEvent } from 'react';

import type { GameId } from '../lib/types.ts';
import {
  beforeIdAt,
  collectCapRects,
  collectSlotRects,
  isRealDragLeave,
  NO_GRID,
  onDragStop,
  parseGid,
  slotGridOf,
  slotShiftTransform,
  startGhostDrag,
} from './dragHtml5.ts';
import type { BeforeId, CapRect, GhostVM, SlotGrid } from './dragHtml5.ts';
import { capsuleDragVis } from '../store/selectors.d3.ts';
import type { CapsuleDragVis } from '../store/selectors.d3.ts';

export interface BoardDndState {
  /** the game being dragged, `0` when nothing is */
  dragId: GameId | 0;
  /** the row key under the pointer, `''` when the pointer is off every row */
  overRow: string;
  /** insert BEFORE this game id; `0` = append (§5.4 — an id, not an index) */
  overBeforeId: BeforeId;
}

export type BoardDndAction =
  | { type: 'dragStart'; id: GameId }
  | { type: 'dragEnd' }
  | { type: 'over'; row: string; before: BeforeId }
  | { type: 'leaveRow'; row: string };

export const BOARD_DND_IDLE: BoardDndState = { dragId: 0, overRow: '', overBeforeId: 0 };

/**
 * Pure, and exported so the transition table can be asserted without a DOM.
 * Returns the SAME object whenever a transition is a no-op — see note 1 above.
 */
export function boardDndReducer(st: BoardDndState, a: BoardDndAction): BoardDndState {
  switch (a.type) {
    case 'dragStart':
      return st.dragId === a.id && !st.overRow && !st.overBeforeId
        ? st
        : { dragId: a.id, overRow: '', overBeforeId: 0 };
    case 'dragEnd':
      return st.dragId || st.overRow || st.overBeforeId ? BOARD_DND_IDLE : st;
    case 'over':
      // A dragover on a row nothing is being dragged over is not reachable
      // through the props below (they bail on `!dragId`), but the reducer is
      // exported, so it guards anyway.
      if (!st.dragId) return st;
      if (st.overRow === a.row && st.overBeforeId === a.before) return st;
      return { ...st, overRow: a.row, overBeforeId: a.before };
    case 'leaveRow':
      // Only the row that owns the highlight may clear it: `dragleave` on the
      // row you just left arrives AFTER `dragenter`/`dragover` on the one you
      // moved onto, so an unguarded clear would blank the new row.
      return st.overRow === a.row ? { ...st, overRow: '', overBeforeId: 0 } : st;
    default:
      return st;
  }
}

/** What a draggable capsule spreads onto its element. */
export interface DragSourceProps {
  draggable: true;
  'data-gid': string;
  onDragStart: (ev: ReactDragEvent<Element>) => void;
  onDragEnd: () => void;
}

/**
 * What a drop-target row spreads onto its element.
 *
 * `onDragEnter` IS NOT OPTIONAL, and leaving it out cost us a bug (2026-08-28).
 *
 * Chromium does not re-hit-test a stationary pointer during a drag — hold still
 * over a row for two seconds and it dispatches literally nothing. The landing
 * slot, though, keeps animating for 160ms after your last movement, and it
 * slides a DIFFERENT capsule under the now-motionless cursor. At mouseup
 * Chromium hit-tests once, finds that different element, and fires `dragenter`
 * on it. An unhandled `dragenter` is a REFUSED drop target: the current target
 * is cleared, and the release comes out as `dragend` with `dropEffect: 'none'`
 * instead of `drop`. The move silently does nothing.
 *
 * Measured: a 1px jiggle after the animation settles fixes every failing case,
 * and so does answering `dragenter` — which is what these do. Both halves of
 * the contract have to be there, because `dragover` alone only keeps a target
 * that Chromium has already accepted.
 */
export interface DropRowProps {
  onDragEnter: (ev: ReactDragEvent<Element>) => void;
  onDragOver: (ev: ReactDragEvent<Element>) => void;
  onDragLeave: (ev: ReactDragEvent<Element>) => void;
  onDrop: (ev: ReactDragEvent<Element>) => void;
}

/** What an order-less drop zone spreads onto its element. */
export interface DropZoneProps {
  onDragEnter: (ev: ReactDragEvent<Element>) => void;
  onDragOver: (ev: ReactDragEvent<Element>) => void;
  onDrop: (ev: ReactDragEvent<Element>) => void;
}

/** A capsule's drag-time look, plus the transform that opens the landing slot. */
export interface CapsuleVis extends CapsuleDragVis {
  /** `''` at rest, else a `translate(...)` carrying it to its slot in the move */
  shift: string;
}

export interface BoardDnd extends BoardDndState {
  /** `!!dragId` — the flag every §5.5 value keys off */
  dragOn: boolean;
  dragProps: (id: GameId, ghost: () => GhostVM) => DragSourceProps;
  rowProps: (rowKey: string, onDrop: (id: GameId, before: BeforeId) => void) => DropRowProps;
  zoneProps: (onDrop: (id: GameId) => void) => DropZoneProps;
  /**
   * One capsule's appearance, given the row it is in and that row's resting
   * order. `rowIds` must be the row AS RENDERED — the lattice is measured from
   * those same capsules.
   */
  capVis: (rowKey: string, rowIds: readonly GameId[], id: GameId) => CapsuleVis;
}

export function useHtml5BoardDnd(): BoardDnd {
  const [state, dispatch] = useReducer(boardDndReducer, BOARD_DND_IDLE);

  // Always-current mirror for the event handlers (note 2).
  const ref = useRef(state);
  ref.current = state;

  /**
   * Each row's resting slot lattice, measured on the first `dragover` it sees
   * and reused for the rest of the drag.
   *
   * Measuring ONCE is not a shortcut, it is the invariant: inside the dragged
   * capsule's own row nothing changes size while the drag is open (see
   * `capsuleDragVis`'s `frozen` arm), so the lattice read at the start is still
   * true at the end. Re-measuring would read the SHIFTED capsules and feed
   * their transforms back into the geometry that produced them.
   */
  const grids = useRef(new Map<string, SlotGrid>());
  /** The same capture, kept whole: the row-relative resting rects (hit-test). */
  const rests = useRef(new Map<string, CapRect[]>());

  // The drag can end anywhere — on a row, on empty page, on another window, or
  // with Escape. `dragHtml5` owns the window `dragend`/`drop` listeners and
  // notifies here so the local state clears in every one of those cases.
  useEffect(
    () =>
      onDragStop(() => {
        grids.current.clear();
        rests.current.clear();
        dispatch({ type: 'dragEnd' });
      }),
    [],
  );

  const dragProps = useCallback(
    (id: GameId, ghost: () => GhostVM): DragSourceProps => ({
      draggable: true,
      // Load-bearing: `collectCapRects` hit-tests on this attribute.
      'data-gid': String(id),
      onDragStart: (ev) => {
        startGhostDrag(ev, ghost());
        dispatch({ type: 'dragStart', id });
      },
      onDragEnd: () => dispatch({ type: 'dragEnd' }),
    }),
    [],
  );

  const rowProps = useCallback(
    (rowKey: string, onDrop: (id: GameId, before: BeforeId) => void): DropRowProps => ({
      // Accept the target Chromium re-hit-tests us with at mouseup. See
      // `DropRowProps` — without this the release is a `dragend`, not a `drop`.
      onDragEnter: (ev) => {
        ev.preventDefault();
      },
      onDragOver: (ev) => {
        // Without `preventDefault` the browser refuses the drop outright.
        ev.preventDefault();
        const { dragId } = ref.current;
        if (!dragId) return;
        const row = ev.currentTarget;
        // Is this the dragged capsule's OWN row? Only that row is frozen, and
        // only a frozen row may be hit-tested against a cached lattice.
        const frozen = !!row.querySelector('[data-gid="' + String(dragId) + '"]');

        let before: BeforeId;
        if (frozen) {
          let rest = rests.current.get(rowKey);
          if (!rest) {
            rest = collectSlotRects(row);
            rests.current.set(rowKey, rest);
            grids.current.set(rowKey, slotGridOf(rest));
          }
          // Row-relative, to match the cached rects — and immune to the
          // auto-scroll that moves every viewport coordinate mid-drag.
          const base = row.getBoundingClientRect();
          before = beforeIdAt(
            rest.filter((r) => r.gid !== dragId),
            ev.clientX - base.left,
            ev.clientY - base.top,
          );
        } else {
          // A row gaining a capsule really does re-flow, so its live rects are
          // the honest ones: the preview matches what the drop will produce.
          before = beforeIdAt(collectCapRects(row, dragId), ev.clientX, ev.clientY);
        }
        dispatch({ type: 'over', row: rowKey, before });
      },
      onDragLeave: (ev) => {
        if (!isRealDragLeave(ev.currentTarget, ev.relatedTarget)) return;
        dispatch({ type: 'leaveRow', row: rowKey });
      },
      onDrop: (ev) => {
        ev.preventDefault();
        const { dragId, overBeforeId } = ref.current;
        const id = dragId || parseGid(readGid(ev));
        if (!id) return;
        dispatch({ type: 'dragEnd' });
        onDrop(id, overBeforeId);
      },
    }),
    [],
  );

  const zoneProps = useCallback(
    (onDrop: (id: GameId) => void): DropZoneProps => ({
      onDragEnter: (ev) => ev.preventDefault(),
      onDragOver: (ev) => ev.preventDefault(),
      onDrop: (ev) => {
        ev.preventDefault();
        const id = ref.current.dragId || parseGid(readGid(ev));
        if (!id) return;
        dispatch({ type: 'dragEnd' });
        onDrop(id);
      },
    }),
    [],
  );

  /**
   * A capsule's look, and — inside the dragged capsule's own row — the shift
   * that opens the landing slot without touching layout.
   *
   * `to` is read in the POST-REMOVAL order, which is the space `overBeforeId`
   * names ("insert before this one, once I am out of the way"), and is also the
   * index the element ends at in the full row. `beforeIdToIdx` converts the
   * same pair for the commit, so the preview and the write agree by
   * construction rather than by two implementations happening to match.
   */
  const capVis = useCallback(
    (rowKey: string, rowIds: readonly GameId[], id: GameId): CapsuleVis => {
      const { dragId, overRow, overBeforeId } = state;
      const from = dragId ? rowIds.indexOf(dragId) : -1;
      const frozen = from >= 0;
      const vis = capsuleDragVis(dragId, id, overBeforeId, frozen);
      if (!frozen || overRow !== rowKey) return { ...vis, shift: '' };

      const rest = rowIds.filter((x) => x !== dragId);
      const at = overBeforeId ? rest.indexOf(overBeforeId) : -1;
      const to = at < 0 ? rest.length : at;
      const grid = grids.current.get(rowKey) ?? NO_GRID;
      return { ...vis, shift: slotShiftTransform(from, to, rowIds.indexOf(id), grid) };
    },
    [state],
  );

  return useMemo<BoardDnd>(
    () => ({ ...state, dragOn: !!state.dragId, dragProps, rowProps, zoneProps, capVis }),
    [state, dragProps, rowProps, zoneProps, capVis],
  );
}

/** `dataTransfer` can be null, and `getData` can throw in a protected drag. */
function readGid(ev: ReactDragEvent<Element>): string | null {
  try {
    return ev.dataTransfer ? ev.dataTransfer.getData('text/plain') : null;
  } catch {
    return null;
  }
}

export default useHtml5BoardDnd;
